import { pipe } from 'fp-ts/lib/function';
import * as TE from 'fp-ts/lib/TaskEither';

import type { CardHtml } from '../model/domain/CardHtml';
import type { Deck, DeckCard, ResourcesItem } from '../model/domain/Deck';
import type { StudyCard, StudyCardReply, StudySession } from '../model/domain/StudySession';
import type { BSTask } from '../model/types';
import { BSTaskVoid } from '../util/bs-fp';

import CardHtmlConverter from './converter/CardHtmlConverter';
import DeckCardsConverter from './converter/DeckCardsConverter';
import DeckConverter from './converter/DeckConverter';
import ResourcesConverter from './converter/ResourcesConverter';
import StudyCardConverter from './converter/StudyCardConverter';
import StudySessionConverter from './converter/StudySessionConverter';
import RestService, { encodeAsJson } from './RestService';
import { cardHtmlTOResponseDecoder } from './to/CardHtmlTO';
import { deckCardsTOResponseDecoder, decksTOResponseDecoder, resourcesItemsTOResponseDecoder } from './to/DeckTO';
import { studyCardTOResponseDecoder, studySessionTOResponseDecoder } from './to/StudySessionTO';

const getUserLibraryDecks = (): BSTask<Deck[]> => {
  const url = '/app/library/decks?deckType=0';

  return pipe(RestService.get(url), decksTOResponseDecoder, TE.map(DeckConverter.toDomain));
};

const getUserLibraryDeckCards = (deckId: string): BSTask<DeckCard[]> => {
  const url = `/app/library/decks/${deckId}`;

  return pipe(RestService.get(url), deckCardsTOResponseDecoder, TE.map(DeckCardsConverter.toDomain));
};

const getUserLibraryCard = (cardId: string): BSTask<CardHtml> => {
  const url = `/app/library/cards/${cardId}`;

  return pipe(RestService.get(url), cardHtmlTOResponseDecoder, TE.map(CardHtmlConverter.toDomain));
};

const getUserLibraryResources = (): BSTask<ResourcesItem[]> => {
  const url = '/app/library/extra-resources';

  return pipe(RestService.get(url), resourcesItemsTOResponseDecoder, TE.map(ResourcesConverter.toDomain));
};

const getStudySession = (): BSTask<StudySession | null> => {
  const url = '/app/session';

  return pipe(
    RestService.get(url),
    studySessionTOResponseDecoder,
    TE.map((to) => (to ? StudySessionConverter.toDomain(to) : null)),
  );
};

const startStudySession = (): BSTask<StudySession | null> => {
  const url = '/app/session/start';

  return pipe(
    RestService.post(url),
    studySessionTOResponseDecoder,
    TE.map((to) => (to ? StudySessionConverter.toDomain(to) : null)),
  );
};

const finishStudySession = (): BSTask<StudySession | null> => {
  const url = '/app/session/finish';

  return pipe(
    RestService.post(url),
    studySessionTOResponseDecoder,
    TE.map((to) => (to ? StudySessionConverter.toDomain(to) : null)),
  );
};

const deleteStudySession = (): BSTask<void> => {
  const url = '/app/session';

  return pipe(
    RestService.sendDelete(url),
    TE.chain(() => BSTaskVoid),
  );
};

const postAnswerGetNextCard = (body: StudyCardReply | null): BSTask<StudyCard | null> => {
  const url = `/app/session/next`;

  return pipe(
    RestService.post(url, body, encodeAsJson),
    studyCardTOResponseDecoder,
    TE.map((to) => (to ? StudyCardConverter.toDomain(to) : null)),
  );
};

export default {
  deleteStudySession,
  finishStudySession,
  getStudySession,
  getUserLibraryCard,
  getUserLibraryDeckCards,
  getUserLibraryDecks,
  getUserLibraryResources,
  postAnswerGetNextCard,
  startStudySession,
};
